/**
 * Product page: variant picker, subscribe/one-off toggle (Seal uses
 * Shopify's native selling plans), live prices, per-bar price,
 * quantity stepper and image gallery.
 */
(() => {
  const root = document.querySelector('[data-product-root]');
  const dataEl = document.querySelector('[data-product-json]');
  if (!root || !dataEl) return;

  const data = JSON.parse(dataEl.textContent);
  const { product } = data;
  const form = root.querySelector('[data-product-form]');
  const variantInput = form.querySelector('[data-variant-id]');
  const addButton = form.querySelector('[data-add-button]');
  const addLabel = form.querySelector('[data-add-label]');
  const addPrice = form.querySelector('[data-add-price]');
  const unitPriceEl = root.querySelector('[data-unit-price]');
  const planSelect = form.querySelector('[data-selling-plan]');
  const subPriceEl = form.querySelector('[data-sub-price]');
  const oncePriceEl = form.querySelector('[data-once-price]');
  const savingEl = form.querySelector('[data-sub-saving]');
  const stickyBar = document.querySelector('[data-sticky-buy]');
  const stickyPrice = document.querySelector('[data-sticky-price]');

  /** Money formatting using the shop's own money format */
  function formatMoney(cents) {
    const format = data.moneyFormat || '£{{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, type) => {
      switch (type) {
        case 'amount_no_decimals':
          return Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        case 'amount_with_comma_separator':
          return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimals}`;
        case 'amount_no_decimals_with_comma_separator':
          return Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        default:
          return `${withCommas}.${decimals}`;
      }
    });
  }

  function selectedOptions() {
    return product.options.map((_, index) => {
      const checked = form.querySelector(`input[data-option-index="${index}"]:checked`);
      return checked ? checked.value : null;
    });
  }

  function findVariant(options) {
    return product.variants.find((variant) => variant.options.every((value, i) => value === options[i]));
  }

  function currentVariant() {
    if (product.variants.length === 1) return product.variants[0];
    return findVariant(selectedOptions());
  }

  function isSubscribe() {
    const checked = form.querySelector('[data-purchase-type]:checked');
    return Boolean(planSelect) && (!checked || checked.value === 'subscribe');
  }

  function allocationFor(variant) {
    if (!planSelect || !variant) return null;
    const planId = Number(planSelect.value);
    return (variant.selling_plan_allocations || []).find((a) => a.selling_plan_id === planId) || null;
  }

  /** Number of bars in the selected bundle, read from an option like "14 bars" */
  function barsIn(variant) {
    for (const value of variant.options) {
      const match = /(\d+)\s*(bar|bars)\b/i.exec(value);
      if (match) return Number(match[1]);
    }
    return null;
  }

  function render() {
    const variant = currentVariant();

    // Selected value labels next to each option name
    selectedOptions().forEach((value, index) => {
      const label = form.querySelector(`[data-option-value="${index}"]`);
      if (label && value) label.textContent = value;
    });

    if (!variant) {
      addButton.disabled = true;
      addLabel.textContent = data.unavailableLabel;
      addPrice.textContent = '';
      unitPriceEl.textContent = '';
      return;
    }

    variantInput.value = variant.id;
    addButton.disabled = !variant.available;
    addLabel.textContent = variant.available ? data.addLabel : data.soldOutLabel;

    const allocation = allocationFor(variant);
    const subscribe = isSubscribe() && allocation;
    const price = subscribe ? allocation.price : variant.price;

    if (planSelect) {
      planSelect.disabled = !isSubscribe();
      if (subPriceEl && allocation) {
        subPriceEl.innerHTML =
          allocation.compare_at_price > allocation.price
            ? `<s>${formatMoney(allocation.compare_at_price)}</s>${formatMoney(allocation.price)}`
            : formatMoney(allocation.price);
      }
      if (savingEl && allocation && allocation.compare_at_price > allocation.price) {
        const percent = Math.round((1 - allocation.price / allocation.compare_at_price) * 100);
        savingEl.textContent = data.saveLabel.replace(/\{\{\s*percent\s*\}\}/, percent);
        savingEl.hidden = false;
      } else if (savingEl) {
        savingEl.hidden = true;
      }
      if (oncePriceEl) oncePriceEl.textContent = formatMoney(variant.price);
    }

    addPrice.textContent = formatMoney(price);
    if (stickyPrice) stickyPrice.textContent = formatMoney(price);

    const bars = barsIn(variant);
    unitPriceEl.textContent = bars
      ? data.unitLabel.replace(/\{\{\s*price\s*\}\}/, formatMoney(Math.round(price / bars)))
      : '';

    // Show the variant's own image, if it has one
    if (variant.featured_image) goToSlide(variant.featured_image.position - 1);

    // Keep the URL shareable
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    markUnavailable();
  }

  /** Strike through option values that have no available variant given the other selections */
  function markUnavailable() {
    const selected = selectedOptions();
    form.querySelectorAll('input[data-option-index]').forEach((input) => {
      const index = Number(input.dataset.optionIndex);
      const options = [...selected];
      options[index] = input.value;
      const match = findVariant(options);
      input.closest('.pill').classList.toggle('pill--unavailable', !match || !match.available);
    });
  }

  form.addEventListener('change', (event) => {
    if (event.target.matches('[data-option-index], [data-purchase-type], [data-selling-plan]')) render();
  });

  /** Quantity stepper */
  const qtyInput = form.querySelector('.qty__input');
  form.querySelectorAll('[data-qty]').forEach((button) => {
    button.addEventListener('click', () => {
      const next = Math.max(1, (Number(qtyInput.value) || 1) + Number(button.dataset.qty));
      qtyInput.value = next;
    });
  });

  /** Gallery */
  const track = root.querySelector('[data-gallery-track]');
  const slides = track ? [...track.querySelectorAll('[data-slide]')] : [];
  const thumbs = [...root.querySelectorAll('[data-thumb]')];

  function goToSlide(index) {
    if (!track || !slides[index]) return;
    track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: 'smooth' });
  }

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => goToSlide(Number(thumb.dataset.thumb)));
  });

  if (track && thumbs.length) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = slides.indexOf(entry.target);
          thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
        });
      },
      { root: track, threshold: 0.6 }
    );
    slides.forEach((slide) => observer.observe(slide));
  }

  /**
   * Seal Subscriptions injects its own widget (sometimes after load). It is
   * hidden in CSS; disable its fields too so only our purchase options are
   * submitted with the form.
   */
  const SEAL_SELECTOR = '.sls-option-container, .seal-table, [class*="sealsubs-"], [class^="sls-"]';
  function disableSealWidget() {
    root.querySelectorAll(SEAL_SELECTOR).forEach((widget) => {
      widget.setAttribute('aria-hidden', 'true');
      widget.querySelectorAll('input, select, textarea, button').forEach((field) => {
        field.disabled = true;
        field.tabIndex = -1;
      });
    });
  }
  disableSealWidget();
  new MutationObserver(disableSealWidget).observe(root, { childList: true, subtree: true });

  render();

  /**
   * Mobile sticky add-to-basket: show from page load, and hide only while
   * the main Add to basket button is on screen (so the two never show
   * together).
   */
  if (stickyBar && 'IntersectionObserver' in window) {
    const stickyAdd = stickyBar.querySelector('[data-sticky-add]');
    let mainButtonVisible = false;
    const updateSticky = () => {
      stickyBar.hidden = mainButtonVisible;
    };
    new IntersectionObserver(([entry]) => {
      mainButtonVisible = entry.isIntersecting;
      updateSticky();
    }).observe(addButton);
    const syncSticky = () => {
      stickyAdd.disabled = addButton.disabled;
      stickyAdd.textContent = addLabel.textContent;
    };
    form.addEventListener('change', syncSticky);
    syncSticky();
  }
})();
